import { useEffect, useRef, useState, type ReactNode } from "react";
import { setBearerToken, getBearerToken } from "@/lib/auth-store";

/**
 * Unified auth bootstrap for SyncSuite-embedded projects.
 *
 * The static-gateway has already consumed any `?__syncsuite_token=` URL
 * token at the edge and converted it into an HttpOnly `__syncsuite_session`
 * cookie (Partitioned, so it works in iframes AND new tabs alike). By the
 * time this component mounts, either:
 *
 *   a) We already have a bearer token in memory (HMR / re-render) → done.
 *   b) The gateway cookie is set → POST /api/syncsuite-login, backend
 *      verifies the cookie against mothership, signs in via BetterAuth,
 *      returns a bearer token we stash in memory.
 *   b2) The cookie login failed and we are INSIDE the dashboard's iframe →
 *      ask the dashboard for a single-use bootstrap token over postMessage
 *      and POST it in the body (the backend's /exchange path). Safari 18.5
 *      through 26.1 rejects the gateway's Partitioned cookie outright and
 *      older WebKit blocks every third-party cookie, so in those browsers
 *      the pane never reaches (b); the handoff needs no cookie at all.
 *   c) No cookie (app opened without a token, e.g. direct URL visit to
 *      a live project) → if we're the top-level page, reload ONCE with
 *      `?__syncsuite_sso=1` — SyncSuite's edge sees the marker and
 *      bounces through the dashboard's single sign-on, which sets the
 *      cookie and strips the URL clean (SSO "pull" flow). If that
 *      round-trip still can't sign us in (not logged in to SyncSuite /
 *      no access to this workspace), fall through to:
 *   d) A friendly error with instructions.
 *
 * No URL-token stripping and no token in any URL: the gateway normalizes
 * the happy path into a cookie before we ever run, and the one fallback
 * (b2) moves a token across the frame boundary, never through a URL.
 */

// Tell the embedding dashboard the pane could not sign in, so it can show
// its own notice ("open in a new tab") instead of a dead frame. No secrets
// in the message, so "*" is fine.
function notifyParentAuthFailed(reason: string): void {
  // Only for a REFUSED sign-in (401). A 5xx or a dead network is the app's
  // server, not the browser, and must never read as "your browser blocked".
  if (window.self === window.top) return;
  try { window.parent.postMessage({ type: "syncsuite-preview-auth-failed", reason }, "*"); } catch { /* ignore */ }
}

// One-shot guard so the SSO reload can never loop: set before we redirect,
// cleared on a successful login so a later expiry can self-heal again.
const SSO_TRIED_KEY = "__syncsuite_sso_tried";

function trySsoRedirect(): boolean {
  // Only when we own the whole tab — inside the dashboard's iframe the
  // parent workspace drives auth, and hijacking the top window from an
  // embedded frame would navigate the customer's dashboard away.
  if (window.self !== window.top) return false;
  try {
    if (sessionStorage.getItem(SSO_TRIED_KEY)) return false;
    sessionStorage.setItem(SSO_TRIED_KEY, "1");
  } catch {
    return false; // storage unavailable → never risk a reload loop
  }
  const u = new URL(window.location.href);
  u.searchParams.set("__syncsuite_sso", "1");
  window.location.replace(u.toString());
  return true;
}
export function AuthGate({ children }: { children: ReactNode }) {
  const [authenticated, setAuthenticated] = useState(() => !!getBearerToken());
  const [error, setError] = useState<string | null>(null);
  // Prevents a double-mount (React StrictMode, HMR) from firing the
  // login fetch twice and racing against itself.
  const loginStarted = useRef(false);

  useEffect(() => {
    if (getBearerToken()) { setAuthenticated(true); return; }
    if (loginStarted.current) return;
    loginStarted.current = true;

    // DEV BYPASS: When VITE_DEV_AUTH_BYPASS=1, skip the SyncSuite handshake
    // and stamp a fake bearer token. The backend honors a matching env var
    // (CRM_DEV_AUTH_BYPASS=1) so the rest of the API works without a real
    // BetterAuth session. Both sides MUST be enabled for this to work, and
    // both only work on your own computer: here, only when the page itself
    // is served from localhost (a SyncSuite test copy never is).
    const onLocalhost = ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname);
    if (import.meta.env.VITE_DEV_AUTH_BYPASS === "1" && import.meta.env.MODE !== "production" && onLocalhost) {
      // eslint-disable-next-line no-console
      console.warn("[AuthGate] ⚠️  VITE_DEV_AUTH_BYPASS=1 — skipping login");
      setBearerToken("dev-bypass-token");
      setAuthenticated(true);
      return;
    }

    // HTTP statuses of every failed attempt, so the final error can tell a
    // refused sign-in (all 401) from a backend that is down (5xx / network).
    const statuses: number[] = [];
    const login = (token?: string) =>
      fetch("/api/syncsuite-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Empty body — the gateway's __syncsuite_session cookie IS the
        // credential. credentials:"include" ensures the cookie is sent
        // even when this file is served from a cross-origin CDN. With a
        // token (the dashboard handoff below) the body is the credential.
        credentials: "include",
        body: JSON.stringify(token ? { token } : {}),
      }).then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const err = new Error((body as { error?: string }).error || `Login failed (${res.status})`) as Error & { status?: number };
          err.status = res.status;
          statuses.push(res.status);
          throw err;
        }
        const data = (await res.json()) as { bearerToken?: string };
        if (!data.bearerToken) throw new Error("No bearer token in response");
        setBearerToken(data.bearerToken);
        setAuthenticated(true);
        try { sessionStorage.removeItem(SSO_TRIED_KEY); } catch { /* ignore */ }
      });

    // (b2) Cookie-less fallback for the dashboard pane: the dashboard mints
    // the same single-use token it would otherwise put on the iframe URL
    // and posts it to us. Only the embedder can be window.parent, and we
    // accept nothing but a string token from it.
    const loginViaParentToken = (): Promise<void> =>
      new Promise<string>((resolve, reject) => {
        if (window.self === window.top) { reject(new Error("not framed")); return; }
        const cleanup = () => { window.clearTimeout(timer); window.removeEventListener("message", onMessage); };
        const timer = window.setTimeout(() => { cleanup(); reject(new Error("No sign-in token from the dashboard")); }, 6000);
        const onMessage = (e: MessageEvent) => {
          if (e.source !== window.parent) return;
          const d = e.data as { type?: unknown; token?: unknown } | null;
          if (!d || d.type !== "syncsuite-token" || typeof d.token !== "string") return;
          cleanup();
          resolve(d.token);
        };
        window.addEventListener("message", onMessage);
        window.parent.postMessage({ type: "syncsuite-request-token" }, "*");
      }).then((token) => login(token));

    login()
      .catch(
        // One silent retry after 1.5s. The login leans on a mothership
        // verify round-trip that can transiently flake (timeout, backend
        // restart); historically that surfaced as "Invalid or expired
        // preview session" which a manual refresh fixed instantly. The
        // retry is that refresh, automated.
        () => new Promise((r) => setTimeout(r, 1500)).then(() => login())
      )
      .catch(() => loginViaParentToken())
      .catch((err: Error) => {
        const refused = statuses.length > 0 && statuses.every((st) => st === 401);
        // A server that answered 5xx (or not at all) is not a sign-in
        // problem: an SSO reload would only loop through the same outage.
        if (refused && trySsoRedirect()) return; // navigating away; keep the spinner up
        console.error("[AuthGate] Login failed:", err);
        if (refused) notifyParentAuthFailed(err.message);
        setError(refused ? err.message : `server:${err.message}`);
      });
  }, []);

  if (error) {
    // Inside the dashboard pane the cause is almost always a browser that
    // would not hold the sign-in cookie (older Safari); a new tab is a
    // first-party visit that signs in everywhere.
    const framed = typeof window !== "undefined" && window.self !== window.top;
    const serverDown = error.startsWith("server:");
    const detail = serverDown ? error.slice("server:".length) : error;
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--color-fg-muted)" }}>
        <p>
          {serverDown
            ? "The app's server isn't answering right now. Try again in a moment."
            : framed ? "Unable to sign in inside this pane." : "Unable to sign in. Please open this app from your SyncSuite dashboard."}
        </p>
        {serverDown && (
          <p style={{ marginTop: 12 }}>
            <button type="button" onClick={() => window.location.reload()} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid var(--color-border)", background: "transparent", color: "inherit", cursor: "pointer", fontSize: 13 }}>Try again</button>
          </p>
        )}
        {framed && !serverDown && (
          <p style={{ marginTop: 12 }}>
            <a
              href={window.location.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "inline-block", padding: "8px 14px", borderRadius: 8, background: "var(--color-accent, #7c3aed)", color: "#fff", textDecoration: "none", fontWeight: 600, fontSize: 13 }}
            >
              Open in a new tab
            </a>
          </p>
        )}
        <p style={{ fontSize: 12, marginTop: 8, color: "var(--color-fg-subtle)" }}>{detail}</p>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        background: "var(--color-bg)",
      }}>
        <div style={{
          width: 24,
          height: 24,
          border: "2px solid var(--color-border)",
          borderTopColor: "var(--color-fg-muted)",
          borderRadius: "50%",
          animation: "spin 0.6s linear infinite",
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  return <>{children}</>;
}
